import { defineStore } from 'pinia'
import { useAuthStore } from './useAuthStore'
import type { VaccinationRecord } from '../types'
import {
  getCollection,
  setDocument,
  updateDocument,
  deleteDocument,
  serverTimestamp,
} from '../services/firestoreService'

export const useVaccineStore = defineStore('vaccine', {
  state: () => ({
    records: [] as VaccinationRecord[],
    loaded: false,
  }),

  actions: {
    async loadRecords() {
      const auth = useAuthStore()
      // 使用 dataOwnerUid：guest 讀 owner 的資料，owner 讀自己的資料
      if (!auth.dataOwnerUid) {
        return
      }

      const path = `users/${auth.dataOwnerUid}/vaccines`

      try {
        const records = await getCollection<Omit<VaccinationRecord, 'id'>>(path)

        this.records = records as VaccinationRecord[]
        this.loaded = true
      } catch (error: any) {
        console.error('loadRecords 失敗:', {
          message: error?.message,
          code: error?.code,
          name: error?.name,
          type: typeof error
        })
        // 即使失敗也標記為已載入（空數組）
        this.loaded = true
        this.records = []
      }
    },

    async setRecord(record: VaccinationRecord) {
      const auth = useAuthStore()
      if (!auth.dataOwnerUid) return

      // 先更新本地狀態，提供即時反饋
      this.records.push(record)

      // 然後保存到 Firestore（帶 updatedBy 供 Cloud Functions 推播排除用）
      await setDocument(
        `users/${auth.dataOwnerUid}/vaccines/${record.id}`,
        {
          ...record,
          updatedAt: serverTimestamp(),
          updatedBy: auth.uid,
        }
      )
    },

    async updateRecord(id: string, data: Partial<VaccinationRecord>) {
      const auth = useAuthStore()
      if (!auth.dataOwnerUid) return

      await updateDocument(
        `users/${auth.dataOwnerUid}/vaccines/${id}`,
        {
          ...data,
          updatedAt: serverTimestamp(),
          updatedBy: auth.uid,
        }
      )

      const target = this.records.find(r => r.id === id)
      if (target) {
        Object.assign(target, data)
      }
    },

    async deleteRecordsByBabyId(babyId: string) {
      const auth = useAuthStore()
      if (!auth.dataOwnerUid) return

      const recordsToDelete = this.records.filter(r => r.babyId === babyId)

      for (const record of recordsToDelete) {
        await deleteDocument(`users/${auth.dataOwnerUid}/vaccines/${record.id}`)
      }

      this.records = this.records.filter(r => r.babyId !== babyId)
    },

    async deleteAllRecords() {
      const auth = useAuthStore()
      // 帳號刪除時使用 auth.uid（自己的資料），避免 guest 誤刪 owner 資料
      if (!auth.uid) return

      const path = `users/${auth.uid}/vaccines`
      const records = await getCollection(path)

      for (const record of records) {
        await deleteDocument(`users/${auth.uid}/vaccines/${record.id}`)
      }

      this.records = []
      this.loaded = false
    },
  },
})
